import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { formatPKR } from "@/lib/api";
import { fromPrice, fromBasePrice, hasVariantPricing, hasAnySale } from "@/lib/beddingConfig";

function resolveImage(url) {
  if (!url) return null;
  if (url.startsWith("http") || url.startsWith("data:")) return url;
  return `${process.env.REACT_APP_BACKEND_URL}${url}`;
}

export default function ProductCard({ product }) {
  const img = product.images?.[0] ? resolveImage(product.images[0]) : null;
  const showFrom = hasVariantPricing(product);
  const finalPrice = showFrom ? fromPrice(product) : (
    product.sale_price && product.sale_price < product.price ? product.sale_price : product.price
  );
  const basePrice = showFrom ? fromBasePrice(product) : product.price;
  const onSale = hasAnySale(product) || (product.sale_price > 0 && product.sale_price < product.price);
  const discountPct = onSale && basePrice > 0 ? Math.round(((basePrice - finalPrice) / basePrice) * 100) : 0;
  const outOfStock = Number(product.stock || 0) <= 0;

  return (
    <Link
      to={`/product/${product.id}`}
      className="group flex flex-col gap-3 sm:gap-4"
      data-testid={`product-card-${product.id}`}
    >
      <div className="relative aspect-[4/5] overflow-hidden bg-cream-warm">
        {img ? (
          <img
            src={img}
            alt={product.name}
            loading="lazy"
            decoding="async"
            sizes="(max-width: 640px) 48vw, (max-width: 1024px) 33vw, 25vw"
            className={`w-full h-full object-cover transition-transform duration-700 group-hover:scale-105 ${outOfStock ? "opacity-70" : ""}`}
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <span className="font-display text-lg sm:text-2xl text-sage">Bedding Lounge</span>
          </div>
        )}
        {outOfStock && (
          <div className="absolute top-2 left-2 sm:top-3 sm:left-3 bg-red-600 text-cream text-[9px] sm:text-[10px] uppercase tracking-[0.16em] sm:tracking-[0.2em] font-semibold px-2 py-1" data-testid={`product-card-oos-${product.id}`}>
            Out of Stock
          </div>
        )}
        {!outOfStock && onSale && discountPct > 0 && (
          <div className="absolute top-2 left-2 sm:top-3 sm:left-3 bg-rose-dark text-cream text-[9px] sm:text-[10px] uppercase tracking-[0.16em] sm:tracking-[0.18em] font-bold px-2 py-1 shadow-sm" data-testid={`product-card-sale-${product.id}`}>
            {discountPct}% Off
          </div>
        )}
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-ink/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        <div className="absolute bottom-4 left-4 right-4 hidden sm:flex items-center justify-between text-cream opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all duration-300">
          <span className="text-xs uppercase tracking-[0.18em] font-semibold">View Details</span>
          <ArrowRight size={14} />
        </div>
      </div>
      <div className="flex flex-col">
        <h3 className="font-display text-base sm:text-xl text-ink leading-tight" data-testid={`product-name-${product.id}`}>
          {product.name}
        </h3>
        {product.sub_type && (
          <div className="label-eyebrow !text-[9px] sm:!text-[10px] !text-ink-soft mt-1">{product.sub_type}</div>
        )}
        <div className="mt-1.5 sm:mt-2 flex items-center justify-between flex-wrap gap-x-2 gap-y-1">
          <div className="flex items-baseline gap-1.5 sm:gap-2" data-testid={`product-price-${product.id}`}>
            {showFrom && <span className="text-[9px] sm:text-[10px] uppercase tracking-widest text-ink-soft">From</span>}
            <span className={`font-sans text-sm sm:text-base ${outOfStock ? "text-ink-soft line-through" : "text-ink"}`}>
              {formatPKR(finalPrice)}
            </span>
            {!outOfStock && onSale && basePrice > finalPrice && (
              <span className="font-sans text-xs sm:text-sm text-ink-soft line-through" data-testid={`product-card-base-${product.id}`}>
                {formatPKR(basePrice)}
              </span>
            )}
          </div>
          {outOfStock ? (
            <span className="text-[10px] sm:text-xs uppercase tracking-[0.18em] font-semibold text-red-600" data-testid={`product-card-sold-out-${product.id}`}>
              Sold Out
            </span>
          ) : (
            <span className="hidden sm:inline text-xs uppercase tracking-[0.18em] font-semibold text-sage group-hover:text-sage-dark transition-colors">
              Shop →
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}
